import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useNavigate, useParams } from "react-router-dom";
import { formDataAtom } from "../App";
import { MedicalVisit } from "../types/FormTypes";
import * as Yup from "yup";
import { useRef } from "react";
import { useIsAddMoreTrue } from "./AddMore";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

import {
  Box,
  ErrorList,
  Button,
  handleSubmitClick,
  Progressbar,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  MultipleFileUploadField,
  Heading,
} from "@ogt/ui";

export const medicalVisitAtom = atom<MedicalVisit>({
  certificate: [],
  wayOfTravel: "",
  receipts: [],
  travelDescription: "",
  comments: "",
  dateTo: "",
  dateFrom: "",
  otherFiles: [],
});

export default function AddCertificate() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [isAddMoreTrue] = useIsAddMoreTrue();
  const { id } = useParams();
  const index = id === undefined ? 0 : Number.parseInt(id) - 1;
  const [formData, setFormData] = useAtom(medicalVisitAtom);
  const [mainFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const progress = isAddMoreTrue ? 87 : 37;
  return (
    <div>
      <Formik<MedicalVisit>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
          });
          navigate("../om-resan");
        }}
        initialValues={{
          ...(mainFormData.medicalVisits[index] ?? formData),
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          certificate: Yup.array().min(
            1,
            "Vänligen bifoga ett intyg innan du går vidare."
          ),
        })}
      >
        {({ errors, touched, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-40 w-full max-w-xs mb-4">
                {/* Stay on same percentage if choosen to add more medicalvisits */}
                {isAddMoreTrue ? (
                  <Progressbar percentage={progress} />
                ) : (
                  <Progressbar percentage={progress} />
                )}
              </div>
            </div>
            <Form ref={formRef}>
              <Box>
                <>
                  <Heading progress={progress} headingRef={h2Ref} focusable>
                    Vårdbesök
                  </Heading>

                  <ErrorList errors={errors} />

                  <Heading as="h3">Intyg för vårdbesök från vårdgivare</Heading>

                  <p>
                    Du måste ha intyg eller kvitto som styrker ditt vårdbesök,
                    kallelser godkänns ej som kvitto. Patienter med frikort
                    skall begära kvitto. Intyget ska innehålla patientens
                    personuppgifter, datum för vårdbesöket samt vårdgivare.
                  </p>
                  <p>
                    Du kan bifoga ett intyg i taget. Om du har flera intyg och
                    vill ansöka om ersättning för flera vårdbesök samtidigt,
                    kommer du att få en fråga om det senare i formuläret.
                  </p>

                  <div className="mb-6">
                    <Accordion allowAllClosed type="single">
                      <AccordionItem value="missing">
                        <AccordionTrigger>
                          Om du saknar ett intyg måste du kontakta din klinik
                          (där sjukvårdsbesöket har ägt rum) och be dem att
                          skicka intyget till dig. Se till att intyget
                          innehåller:
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul>
                            <li>Patientens namn (och vårdnadshavarens)</li>
                            <li>
                              Patientens personnummer (och vårdnadshavarens)
                            </li>
                            <li>Vårdgivare</li>
                            <li>Datum för vårdbesök</li>
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>

                  <MultipleFileUploadField
                    inputName="certificate"
                    currentFiles={values.certificate}
                    multiple={false}
                    type="intyg"
                    label="Bifoga ett intyg för ditt vårdbesök (Obligatorisk)"
                    inputId="certificate"
                  />
                </>
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn secondary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
